import { useEffect, useState } from 'react';
import { useNavigation, useOne, useTranslate } from '@refinedev/core';
import { Alert, Button, Drawer, Grid, Skeleton, Tabs, theme } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import type { PublicationDetailOut } from '@/api/generated/schemas';
import { PublicationHero } from '@/components/user_catalog/publications/PublicationHero';
import { DrawerGeneralTab } from './tabs/DrawerGeneralTab';
import { DrawerFormatsTab } from './tabs/DrawerFormatsTab';
import { DrawerAvailabilityTab } from './tabs/DrawerAvailabilityTab';

const RESOURCE = 'user_catalog.publications';

const DRAWER_TABS = ['general', 'formats', 'availability'] as const;
type DrawerTab = (typeof DRAWER_TABS)[number];

interface PublicationDrawerProps {
  publicationId: number | null;
  onClose: () => void;
}

/** Read-only quick view of a publication, opened from references across
 * the admin without leaving the current page. */
export const PublicationDrawer = ({
  publicationId,
  onClose,
}: PublicationDrawerProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { show } = useNavigation();
  const [activeTab, setActiveTab] = useState<DrawerTab>('general');

  const { result: record, query } = useOne<PublicationDetailOut>({
    resource: RESOURCE,
    id: publicationId ?? undefined,
    queryOptions: { enabled: publicationId != null },
  });

  useEffect(() => {
    setActiveTab('general');
  }, [publicationId]);

  const tabBody = (tab: DrawerTab, data: PublicationDetailOut) => {
    switch (tab) {
      case 'general':
        return <DrawerGeneralTab record={data} />;
      case 'formats':
        return <DrawerFormatsTab record={data} />;
      case 'availability':
        return <DrawerAvailabilityTab record={data} />;
    }
  };

  const body = () => {
    if (query.isError) {
      return (
        <Alert
          type="error"
          showIcon
          message={t('user_catalog.publications.drawer.load_error')}
        />
      );
    }
    // `record` may be a stale previous publication while the next loads.
    if (!record || record.id !== publicationId) {
      return <Skeleton active avatar={{ shape: 'square', size: 96 }} />;
    }
    return (
      <>
        <PublicationHero record={record} compact />
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as DrawerTab)}
          style={{ marginTop: token.marginSM }}
          items={DRAWER_TABS.map((key) => ({
            key,
            label: t(`user_catalog.publications.tabs.${key}`),
            children: tabBody(key, record),
          }))}
        />
      </>
    );
  };

  return (
    <Drawer
      open={publicationId != null}
      onClose={onClose}
      width={screens.md ? 720 : '100%'}
      destroyOnHidden
      title={
        publicationId != null
          ? t('user_catalog.publications.drawer.title', { id: publicationId })
          : undefined
      }
      extra={
        publicationId != null && (
          <Button
            icon={<ExportOutlined />}
            onClick={() => show(RESOURCE, publicationId)}
          >
            {t('user_catalog.publications.drawer.open_full')}
          </Button>
        )
      }
    >
      {publicationId != null && body()}
    </Drawer>
  );
};
